import { AttachmentType } from "@/modules/beneficiary/common/enum";
import type { IBeneficiaryItem } from "@/modules/beneficiary/common/types";
import { BeneficiaryType, CloudflareAsset } from "@/shared/common/enums";
import { CloudFlareImage } from "@/shared/components";
import { MoreIcon } from "@/shared/components/SvgIcons";
import { formatCardNumber } from "@/shared/helpers";
import { useNotify } from "@/shared/hooks";
import { ButtonIcon } from "@ab.uitools/ui-library/components/ButtonIcon";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Menu } from "@ab.uitools/ui-library/components/Menu";
import { IconCopy } from "@ab.uitools/ui-library/components/SVGIcons/IconCopy";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import classNames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  item: IBeneficiaryItem & { id: string };
  index: number;
  type: AttachmentType;
  isPrivate?: boolean;
  onTransfer: (item: IBeneficiaryItem & { id: string }) => void;
  onEdit: (item: IBeneficiaryItem & { id: string }) => void;
  onDelete: (item: IBeneficiaryItem & { id: string }) => void;
}

export const BeneficiaryAccountCardItem = ({
  item,
  index,
  type,
  isPrivate,
  onTransfer,
  onEdit,
  onDelete,
}: Props) => {
  const { t } = useTranslation();
  const { showSuccessSnackbar } = useNotify();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [menuRef, setMenuRef] = useState<HTMLDivElement | null>(null);

  const imagePath =
    type === AttachmentType.Account ? CloudflareAsset.Account : CloudflareAsset.MasterCard;

  const menuItems = [
    ...(isPrivate
      ? [
        {
          label: t("general.edit"),
          value: "edit_attachment",
          handler: () => {
            setIsMenuOpen(false);
            onEdit(item);
          },
          iconProps: {
            Component: (props: ISVGIconProps) => (
              <CloudFlareImage {...props} path={CloudflareAsset.Edit}/>
            ),
          },
        },
      ]
      : []),
    {
      label: t("general.transfer"),
      value: "transfer_attachment",
      handler: () => {
        setIsMenuOpen(false);
        onTransfer(item);
      },
      iconProps: {
        Component: (props: ISVGIconProps) => (
          <CloudFlareImage {...props} path={CloudflareAsset.CardTransfer}/>
        ),
      },
    },
    {
      label: t("general.copy"),
      value: "copy_attachment",
      handler: () => {
        navigator.clipboard.writeText(item.number);
        setIsMenuOpen(false);
        showSuccessSnackbar(t("general.copied"));
      },
      iconProps: {
        Component: (props: ISVGIconProps) => <IconCopy {...props}/>,
      },
    },
    ...(isPrivate
      ? [
        {
          label: t("general.delete"),
          value: "delete_attachment",
          handler: () => {
            setIsMenuOpen(false);
            onDelete(item);
          },
          iconProps: {
            Component: (props: ISVGIconProps) => (
              <CloudFlareImage {...props} path={CloudflareAsset.Trash}/>
            ),
          },
        },
      ]
      : []),
  ];

  return (
    <div className={classNames(
      "beneficiary-detail-attachment-item flexbox align-items--center px-16",
      { active: isMenuOpen }
    )}>
      <div className="card-account-image flexbox align-items--center justify-content--center">
        <CloudFlareImage path={imagePath}/>
      </div>
      <div className="full-width ml-24">
        {!!index && <Divider color="light" type="primary" isHorizontal/>}
        <div className="flexbox full-width py-16">
          <div className="flexbox flex-direction--column full-width justify-content--center">
            <Text weight="bold" size="medium">
              {item.type === BeneficiaryType.Card ? formatCardNumber({ card: item.number }) : item.number}
            </Text>
            {item.note && (
              <Text className="mt-2" weight="bold" type="secondary" size="small">
                {item.note}
              </Text>
            )}
          </div>
          <div className="flexbox align-items--center ml-8">
            {!!menuItems.length && (
              <div ref={setMenuRef} className="flexbox align-items--center">
                <ButtonIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen((prev) => !prev);
                  }}
                  data-testid="attachment-item-more-btn"
                  data-ga-id="attachment-item-more-btn"
                  data-ga-action="Open attachment item action menu"
                  size="large"
                  className={classNames("item-action-button", { active: isMenuOpen })}
                  iconProps={{
                    Component: MoreIcon,
                    type: "secondary",
                  }}
                />
                <Menu
                  position={Positions.BOTTOM_RIGHT}
                  onClose={() => setIsMenuOpen(false)}
                  isOpen={isMenuOpen}
                  parentRef={menuRef}
                  additionalRef={menuRef}
                  menuItems={menuItems}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
